import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, Check, Loader2, ExternalLink, Link as LinkIcon, CheckCheck, Hourglass } from "lucide-react";
import { MultiPhaseTracker } from '@/components/onboarding/MultiPhaseTracker';
import { PhaseStatus } from '@/components/onboarding/PhaseStatusIndicator';
import { Button } from '@/components/ui/button';
import { toast } from "@/hooks/use-toast";

interface FormData {
  id: string;
  kol_name: string;
  status: string;
  approved?: string;
  status_phase_1?: string;
  status_phase_2?: string;
  status_phase_3?: string;
  status_phase_4?: string;
  status_phase_5?: string;
  status_phase_6?: string;
  status_phase_7?: string;
  test_transfer?: boolean;
  test_transfer_url?: string;
  tx_hash?: string;
  wallet_address?: string;
  wallet_address_network?: string;
  payment_status?: string;
}

const getPhaseStatus = (statusValue?: string): PhaseStatus => {
  if (!statusValue) return "pending";
  
  switch (statusValue) {
    case "completed": return "completed";
    case "in-progress": return "in-progress";
    case "error": return "error";
    default: return "pending";
  }
};

const getPhaseData = (formData: FormData) => {
  return [
    {
      number: 1,
      status: getPhaseStatus(formData.status_phase_1),
      title: "Form Submission",
      description: "Initial submission of creator information",
    },
    {
      number: 2,
      status: getPhaseStatus(formData.status_phase_2),
      title: "Application Review",
      description: "Team reviews your application",
    },
    {
      number: 3,
      status: getPhaseStatus(formData.status_phase_3),
      title: "Invoice Generation",
      description: "Creation of invoice for compensation",
    },
    {
      number: 4,
      status: getPhaseStatus(formData.status_phase_4),
      title: "Test Transaction",
      description: "Small test payment to verify wallet",
    },
    {
      number: 5,
      status: getPhaseStatus(formData.status_phase_5),
      title: "Test Confirmation",
      description: "Verification of successful test payment",
    },
    {
      number: 6,
      status: getPhaseStatus(formData.status_phase_6),
      title: "Final Payment",
      description: "Processing of full compensation amount",
    },
    {
      number: 7,
      status: getPhaseStatus(formData.status_phase_7),
      title: "Completion",
      description: "All onboarding steps successfully completed",
    },
  ];
};

// Determine current phase based on status values
const getCurrentPhase = (formData: FormData): number => {
  if (formData.status_phase_7 === "completed") return 7;
  if (formData.status_phase_6 === "completed" || formData.status_phase_6 === "in-progress") return 6;
  if (formData.status_phase_5 === "completed" || formData.status_phase_5 === "in-progress") return 5;
  if (formData.status_phase_4 === "completed" || formData.status_phase_4 === "in-progress") return 4;
  if (formData.status_phase_3 === "completed" || formData.status_phase_3 === "in-progress") return 3;
  if (formData.status_phase_2 === "completed" || formData.status_phase_2 === "in-progress") return 2;
  return 1;
};

// Helper function to return the transaction URL without formatting for test transactions
const getFormattedTransactionUrl = (url?: string, isTestTransaction: boolean = false): string => {
  if (!url) return '';
  
  // For test transactions, always return the URL as-is to avoid modification
  if (isTestTransaction) {
    return url;
  }
  
  // For final transactions (tx_hash), use explorer for transaction hash format
  if (/^0x[a-fA-F0-9]{64}$/.test(url)) {
    return `https://explorer.testnet.mantle.xyz/tx/${url}`;
  }
  
  // For any other URL format, return as-is
  return url;
};

const OnboardingSuccess = () => {
  const [creatorName, setCreatorName] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isConfirmingFinal, setIsConfirmingFinal] = useState<boolean>(false);
  const { formId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOnboardingForm = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching onboarding form with ID:", formId);
        
        if (!formId) {
          setError("No form ID provided");
          return;
        }
        
        // Fetch the onboarding form
        const { data: onboardingForm, error: onboardingError } = await supabase
          .from('onboarding_forms')
          .select('id, kol_name, status, approved, status_phase_1, status_phase_2, status_phase_3, status_phase_4, status_phase_5, status_phase_6, status_phase_7, test_transfer, test_transfer_url, tx_hash, wallet_address, wallet_address_network, payment_status')
          .eq('id', formId)
          .single();

        if (onboardingError) {
          console.error('Error fetching onboarding form:', onboardingError);
          setError("Failed to load form details");
          return;
        }

        if (onboardingForm) {
          console.log("Onboarding form found:", onboardingForm);
          setCreatorName(onboardingForm.kol_name);
          setFormData(onboardingForm);
          
          // Update status_phase_1 to completed if it's still pending
          if (onboardingForm.status_phase_1 === 'pending') {
            await supabase
              .from('onboarding_forms')
              .update({ status_phase_1: 'completed' })
              .eq('id', formId);
            
            // Update local state
            setFormData(prev => prev ? {...prev, status_phase_1: 'completed'} : null);
          }
          
        } else {
          console.error('No onboarding form found with ID:', formId);
          setError("Form not found");
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        setError("Failed to load form details");
      } finally {
        setIsLoading(false);
      }
    };

    if (formId) {
      fetchOnboardingForm();
    }
  }, [formId]);

  const handleConfirmTestTransaction = async () => {
    if (!formId || !formData) return;
    
    try {
      setIsConfirming(true);
      
      console.log("Confirming test transaction for form:", formId);
      
      // Use the edge function with comprehensive update data
      const { data, error: updateError } = await supabase.functions.invoke('update-payment-submissions', {
        body: { 
          form_id: formId,
          update_data: {
            test_transfer: true,
            status_phase_4: 'completed',
            status_phase_5: 'completed',
            status_phase_6: 'in-progress',
          }
        }
      });
      
      if (updateError) {
        console.error("Edge function error:", updateError);
        throw updateError;
      }
      
      console.log("Test transaction confirmed successfully:", data);
      
      // Update local state with comprehensive data
      setFormData(prev => prev ? {
        ...prev, 
        test_transfer: true,
        status_phase_4: 'completed',
        status_phase_5: 'completed',
        status_phase_6: 'in-progress'
      } : null);
      
      toast({
        title: "Test Transaction Confirmed",
        description: "Thank you for confirming. Your final payment process will begin soon.",
      });
      
    } catch (error: any) {
      console.error('Error confirming test transaction:', error);
      toast({
        variant: "destructive",
        title: "Confirmation Failed",
        description: error.message || "Failed to confirm test transaction",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Enhanced final payment confirmation
  const handleConfirmFinalPayment = async () => {
    if (!formId || !formData) return;
    
    try {
      setIsConfirmingFinal(true);
      
      console.log("Confirming final payment for form:", formId);
      
      // Use the dedicated API endpoint for final payment confirmation
      const { data, error: updateError } = await supabase.functions.invoke('update-payment-submissions', {
        body: { 
          form_id: formId,
          confirm_final_payment: true
        }
      });
      
      if (updateError) {
        console.error("Final payment confirmation error:", updateError);
        throw updateError;
      }
      
      console.log("Final payment confirmed successfully:", data);
      
      // Update local state with comprehensive completion data
      setFormData(prev => prev ? {
        ...prev, 
        status_phase_6: 'completed',
        status_phase_7: 'completed',
        status: 'completed',
        payment_status: 'Payment Confirmed'
      } : null);
      
      toast({
        title: "Final Payment Confirmed",
        description: "Thank you for confirming. Your onboarding process is now complete and records have been created!",
      });
      
    } catch (error: any) {
      console.error('Error confirming final payment:', error);
      toast({
        variant: "destructive",
        title: "Confirmation Failed",
        description: error.message || "Failed to confirm final payment",
      });
    } finally {
      setIsConfirmingFinal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dashboard-bg p-4">
        <Card className="w-full max-w-lg p-6 space-y-6 dashboard-card">
          <div className="text-center">
            <div className="h-20 w-24 mx-auto">
              <img 
                src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
                alt="Mantle Logo" 
                className="h-full w-full object-contain"
              />
            </div>
            <p className="mt-4">Loading form details...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dashboard-bg p-4">
        <Card className="w-full max-w-lg p-6 space-y-6 dashboard-card">
          <div className="text-center">
            <div className="h-20 w-24 mx-auto">
              <img 
                src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
                alt="Mantle Logo" 
                className="h-full w-full object-contain"
              />
            </div>
            <div className="mt-6 flex flex-col items-center justify-center text-red-400">
              <AlertCircle className="h-10 w-10 mb-2" />
              <p>{error}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dashboard-bg p-4">
        <Card className="w-full max-w-lg p-6 space-y-6 dashboard-card">
          <div className="text-center">
            <div className="h-20 w-24 mx-auto">
              <img 
                src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
                alt="Mantle Logo" 
                className="h-full w-full object-contain"
              />
            </div>
            <div className="mt-6 flex flex-col items-center justify-center text-yellow-500">
              <AlertCircle className="h-10 w-10 mb-2" />
              <p>Form data not available</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const phases = getPhaseData(formData);
  const currentPhase = getCurrentPhase(formData);
  
  // Enhanced logic for showing test transaction confirmation
  const showTestTransactionConfirmation = 
    formData.test_transfer_url && 
    !formData.test_transfer &&
    formData.status_phase_5 === 'in-progress';

  // Enhanced logic for showing final payment confirmation
  const showFinalPaymentConfirmation =
    formData.tx_hash &&
    formData.status_phase_6 === 'in-progress' &&
    formData.status_phase_7 !== 'completed';

  return (
    <div className="min-h-screen flex items-center justify-center bg-dashboard-bg p-4">
      <Card className="w-full max-w-lg p-6 space-y-6 dashboard-card animate-fade-in">
        <div className="text-center space-y-4">
          <div className="h-20 w-24 mx-auto">
            <img 
              src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
              alt="Mantle Logo" 
              className="h-full w-full object-contain"
            />
          </div>
          
          <div className="flex justify-center">
            {showTestTransactionConfirmation ? (
              <div className="h-16 w-16 flex items-center justify-center">
                <Hourglass className="h-16 w-16 text-amber-500 animate-pulse" />
              </div>
            ) : showFinalPaymentConfirmation ? (
              <div className="h-16 w-16 flex items-center justify-center">
                <Hourglass className="h-16 w-16 text-amber-500 animate-pulse" />
              </div>
            ) : formData.status_phase_3 === "completed" && !formData.test_transfer ? (
              <div className="h-16 w-16 flex items-center justify-center">
                <Loader2 className="h-16 w-16 text-amber-500 animate-spin" />
              </div>
            ) : (
              <Check className="h-16 w-16 text-green-500" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-dashboard-text-primary">
            {showTestTransactionConfirmation
              ? "Test transaction sent!"
              : showFinalPaymentConfirmation
                ? "Final payment sent!"
                : formData.status_phase_3 === "completed" && !formData.test_transfer
                  ? "Invoice submitted successfully!"
                  : formData.status_phase_7 === "completed"
                    ? "Onboarding completed successfully!"
                    : "Thank you for submitting your form!"}
          </h1>
          
          <p className="text-dashboard-text-secondary">
            {creatorName ? `Welcome, ${creatorName}! ` : ''}
            {showTestTransactionConfirmation
              ? "We've sent a test transaction to your wallet to verify the address. Please check your wallet and confirm receipt when you receive it."
              : showFinalPaymentConfirmation
                ? "We've sent the final payment to your wallet. Please confirm receipt when you've received it."
                : formData.status_phase_3 === "completed" && formData.status_phase_4 === "in-progress" && !formData.test_transfer
                  ? "We will send a test transaction to your wallet and let you know when it's ready."
                  : formData.status_phase_7 === "completed"
                    ? "All onboarding steps have been successfully completed. Thank you!"
                    : "Your information has been successfully submitted."}
          </p>

          {formData.payment_status && (
            <div className="flex justify-center">
              <span className={`px-3 py-1 rounded-full text-sm font-medium
                ${formData.payment_status === 'Payment Confirmed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 
                formData.payment_status === 'Payment Sent' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'}`}>
                {formData.payment_status}
              </span>
            </div>
          )}

          <div className="mt-4">
            <MultiPhaseTracker 
              phases={phases} 
              currentPhase={currentPhase} 
              className="mt-6" 
            />
          </div>

          {/* Enhanced Test Transaction Section */}
          {showTestTransactionConfirmation && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <h3 className="font-medium text-amber-800 dark:text-amber-300">Test Transaction Sent!</h3>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                We've sent a small test transaction to your wallet. Please verify you received it and confirm below.
              </p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center text-xs text-amber-700 dark:text-amber-400">
                  <LinkIcon className="h-3 w-3 mr-1" />
                  <span>Wallet: {formData.wallet_address?.substring(0, 8)}...{formData.wallet_address?.substring(formData.wallet_address.length - 6)}</span>
                </div>
                <div className="flex items-center text-xs text-amber-700 dark:text-amber-400">
                  <LinkIcon className="h-3 w-3 mr-1" />
                  <span>Network: {formData.wallet_address_network}</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between mt-3 gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-amber-600 border-amber-300"
                  onClick={() => window.open(getFormattedTransactionUrl(formData.test_transfer_url, true), '_blank')}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  View Transaction
                </Button>
                <Button 
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={handleConfirmTestTransaction}
                  disabled={isConfirming}
                >
                  {isConfirming ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <CheckCheck className="h-3.5 w-3.5 mr-1" />
                      I Received It!
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Enhanced Final Payment Section */}
          {showFinalPaymentConfirmation && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <h3 className="font-medium text-green-800 dark:text-green-300">Final Payment Sent!</h3>
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                Your final payment has been processed. Please verify you received it and confirm below.
              </p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center text-xs text-green-700 dark:text-green-400">
                  <LinkIcon className="h-3 w-3 mr-1" />
                  <span>Wallet: {formData.wallet_address?.substring(0, 8)}...{formData.wallet_address?.substring(formData.wallet_address.length - 6)}</span>
                </div>
                <div className="flex items-center text-xs text-green-700 dark:text-green-400">
                  <LinkIcon className="h-3 w-3 mr-1" />
                  <span>Network: {formData.wallet_address_network}</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between mt-3 gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-green-600 border-green-300"
                  onClick={() => window.open(getFormattedTransactionUrl(formData.tx_hash), '_blank')}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  View Transaction
                </Button>
                <Button 
                  size="sm"
                  className="bg-green-500 hover:bg-green-600 text-white"
                  onClick={handleConfirmFinalPayment}
                  disabled={isConfirmingFinal}
                >
                  {isConfirmingFinal ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <CheckCheck className="h-3.5 w-3.5 mr-1" />
                      I Received It!
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Completed Transaction Section (moved from above) */}
          {formData.tx_hash && !showFinalPaymentConfirmation && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <h3 className="font-medium text-green-800 dark:text-green-300">Transaction Complete!</h3>
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                Your payment has been processed successfully.
              </p>
              <Button 
                size="sm" 
                className="mt-2" 
                variant="outline"
                onClick={() => window.open(getFormattedTransactionUrl(formData.tx_hash), '_blank')}
              >
                View Transaction
              </Button>
            </div>
          )}

          {/* Show a button to go back to the form if they're still on Phase 3 */}
          {formData.status_phase_3 === "in-progress" && (
            <div className="mt-4">
              <Button
                onClick={() => navigate(`/onboarding/${formId}`)}
                variant="outline"
              >
                Continue Form Submission
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OnboardingSuccess;
